import React from "react";
import LeagueStatusLine from "./LeagueStatusLine";
import { getLeagueStatus } from "./leagueStatus";
import { leagueDateLabel, podiumPayouts, topPrizeDisplay } from "./leagueFormat";

// THE CASH GAME PRIZE CARD, in one place.
//
// It started as a block inside the landing page. The client has since asked for
// the same card on the landing page, on the Game details preview and at the top
// of the competition home's right rail — "We need to bring the prize card into
// the competition home page" — so it lives here and those three render it. A
// prize stated three different ways on three screens is exactly the drift the
// client keeps asking us to stop.

// ── the four states ───────────────────────────────────────────────────────
// The client wrote the first three out in full, pill and CTA together:
//
//   "Before the game goes live… ENTRY OPEN (our brand gold and dark teal)
//    Starts in 2d (token color) / CTA: Enter Free
//    Once the game starts, the nonmember state becomes: LIVE Ends in 9h
//    CTA: View Leaderboard
//    For someone who already entered, the live CTA should remain: Enter Game"
//
// …and then replaced the fourth. It used to be a grey "Completed" pill beside
// "Sep 18". Client: "Pill colors for Final pill — Light grey background, Subtle
// grey border, Navy Final text, No status dot. Top-right line shows the
// completion date (e.g., Sep 5) in muted gray. We do not need completed or ended
// next to the date. The Final pill does the job."
//
// The two live states are explicitly unchanged: "you already have the pill and
// font colors for Entry Open and Starting Soon… no change" / "you already have
// the pill and font colors for Live. You have the colors for Ends in. - no
// change".
//
// Gold behind dark teal is StatusPill's `cashToneStyles.soon`, the client's own
// "The pill is our gold (#d7c588), and the font is dark teal (#004c50)"; Live is
// that component's shared `live` value.
const ENTRY_OPEN_PILL = "bg-[#D7C588] text-[#004C50]";
const LIVE_PILL = "border border-green-500 bg-white text-green-600";
const FINAL_PILL = "border border-gray-200 bg-[#F3F4F6] text-[#0F172A]";

// FLAGGED to the client: a live game whose `join_after_start` is on is still
// joinable, and this shows a nonmember "View Leaderboard" there because that is
// what they wrote. Every QUSHi cash game carries "ENTRY: Entry closes when the
// game starts", so no live game reaches that case today.
export const cashEntryState = (game) => {
  const tone = getLeagueStatus(game)?.tone;
  const member = !!game?.isMember;

  if (tone === "ended") {
    return {
      key: "final",
      pill: "Final",
      pillCls: FINAL_PILL,
      cta: "View Results",
      action: "leaderboard",
    };
  }
  if (tone === "live") {
    return {
      key: "live",
      pill: "Live",
      pillCls: LIVE_PILL,
      cta: member ? "Enter Game" : "View Leaderboard",
      action: member ? "league" : "leaderboard",
    };
  }
  return {
    key: "open",
    pill: "Entry Open",
    pillCls: ENTRY_OPEN_PILL,
    cta: member ? "Enter Game" : "Enter Free",
    action: member ? "league" : "join",
  };
};

// The pill, and the status beside it. Client: "The prize cards on these screens
// will have the pill (top left) and status (top right)."
//
// The countdown is LeagueStatusLine — the single definition of the locked status
// tokens (#006565 "Starts in", #35B23C "Ends in") — so "(token color)" is
// satisfied by construction rather than by copying a hex.
//
// A finished game is the one exception, and the client spelled out why: the
// pill already says Final, so the line beside it is the DATE ALONE, in muted
// grey. "Sep 5 (date token, no drift — copy how we do dates everywhere else; no
// year for the current year)" is leagueDateLabel, which is that rule.
export const CashStatusRow = ({ game, state, className = "" }) => {
  const s = state || cashEntryState(game);
  return (
    <div className={`flex items-center justify-between gap-2 ${className}`}>
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 font-montserrat text-xs font-semibold ${s.pillCls}`}
      >
        {/* "No status dot" on Final; Live keeps the one it has. */}
        {s.key === "live" && (
          <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
        )}
        {s.pill}
      </span>
      {s.key === "final" ? (
        <span className="shrink-0 text-sm font-semibold text-tertiary">
          {leagueDateLabel(game?.end_date)}
        </span>
      ) : (
        <LeagueStatusLine game={game} />
      )}
    </div>
  );
};

// The 2nd/3rd payout rows: a compact stack inside one bordered box, the label
// left and the figure right, with a hairline between them.
//
// Returns NOTHING for a game that pays a single winner, which is what makes the
// "winner takes all" variant fall out of the same component — client: "Winner
// take all variant: Show WINNER TAKES ALL, the cash amount, and REAL CASH. Do
// not show a payout box."
//
// `whitespace-nowrap` on the label because "2ND PLACE" wrapped to two lines in
// the card's narrow right-hand column, which the design never does.
export const PayoutRows = ({ rows, className = "mt-4" }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border border-gray-200 px-3 py-2 ${className}`}
    >
      {rows.map((row, index) => (
        <div
          key={row.key}
          className={`flex items-center gap-2 ${
            index > 0 ? "border-t border-divider pt-2" : ""
          }`}
        >
          {/* Silver for second, bronze for third — the mobile design's rails. */}
          <span
            className="w-1 self-stretch rounded-full"
            style={{ backgroundColor: row.rail }}
          />
          <div className="flex flex-1 items-center justify-between gap-2">
            <p className="whitespace-nowrap text-[11px] font-medium uppercase tracking-tight text-secondary">
              {row.label}
            </p>
            <p className="text-[15px] font-black leading-none text-[#0F172A]">
              {row.display}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * The prize itself: the status row, then the figure.
 *
 * `payouts` is off on the competition home, where the client asked for the
 * stripped version — "Prize card content, stripped hero only: pill + status (top
 * row), WINNER TAKES ALL, $100, REAL CASH. No eligible-assets or ends rows in
 * this card, no contest rules."
 *
 * The `mt-6` under the status row is the client's own note about the phone:
 * "Make sure a little padding between the header (pill and the status line), and
 * the start of the content - WINNER TAKES ALL. just like the desktop prize
 * card." It is the desktop card's gap, so one value serves both.
 */
export const CashPrizeHero = ({ game, payouts = true, className = "" }) => {
  const state = cashEntryState(game);
  const top = topPrizeDisplay(game);
  const runners = payouts ? podiumPayouts(game) : [];
  const winnerTakesAll = podiumPayouts(game).length === 0;

  return (
    <div className={className}>
      <CashStatusRow game={game} state={state} />

      <div className="mt-6 text-center">
        <p className="font-montserrat text-[10px] font-black uppercase tracking-[0.2em] text-[#004C50]">
          {winnerTakesAll ? "Winner takes all" : "Top prize"}
        </p>
        <p className="mt-2 font-montserrat text-[40px] font-black leading-none text-[#0F172A]">
          {top || "—"}
        </p>
        {/* "Keep REAL CASH as static supporting text beneath the Top Prize
            amount." */}
        <p className="mt-1 font-montserrat text-[12px] font-extrabold uppercase tracking-widest text-[#004C50]">
          Real cash
        </p>
        <div className="text-left">
          <PayoutRows rows={runners} />
        </div>
      </div>
    </div>
  );
};

export default CashPrizeHero;

/**
 * The phone's version of the prize card: one meta line, not a card.
 *
 * Client: "for the mobile version of a cash game home competition page we will
 * put the prize info in a meta line under the `Mixed · 2 entrants`
 *
 *   $100 prize · Winner takes all
 *   Prize pool · $100 · $50 · $20
 *
 * Winner-take-all game: `$100 prize · Winner takes all` — $100 navy, `prize ·
 * Winner takes all` muted. Tiered game: `Prize pool · $100 · $50 · $20` — here
 * the three dollar figures go navy (they're all money), and the `Prize pool ·`
 * label goes muted. So navy carries the numbers, muted carries the label, same
 * as everywhere else."
 *
 * The two shapes put the label on opposite sides on purpose — that is how the
 * client wrote them — and the rule that decides which is the same
 * `podiumPayouts` the cards use, so a game cannot describe its prize one way
 * here and another way in the rail beside it.
 */
const NAVY = "font-semibold text-[#0F172A]";

export const CashPrizeMetaLine = ({ game, className = "" }) => {
  const top = topPrizeDisplay(game);
  if (!top) return null;
  const runners = podiumPayouts(game);

  return (
    <p className={`text-sm text-secondary ${className}`}>
      {runners.length === 0 ? (
        <>
          <span className={NAVY}>{top}</span> prize · Winner takes all
        </>
      ) : (
        <>
          Prize pool ·{" "}
          {[top, ...runners.map((r) => r.display)].map((amount, i) => (
            <React.Fragment key={amount + i}>
              {i > 0 && " · "}
              <span className={NAVY}>{amount}</span>
            </React.Fragment>
          ))}
        </>
      )}
    </p>
  );
};
